// Job-level progression helpers. See docs/03-jobs-skills §3.5 and docs/06-level-xp §6.6, §6.12, §6.13.
// Permanent stat formula updated by ADR 0006: switched from L0=400 single curve to per-Tier
// statContribution(jobMaxLv, tier) shared with the current-job stat scaling.
import { STAT_GROWTH_VALUE, STAT_KEYS, statContribution, } from '@renavora/shared';
import { JOB_XP_BONUS_MULTIPLIER, MAX_LEVEL_ASYMPTOTE, PERMANENT_STAT_BONUS_COEFF, RESTART_RATIO_BY_SCROLL, RESTART_RATIO_DEFAULT, } from './constants.js';
function assertFinite(name, value) {
    if (!Number.isFinite(value)) {
        throw new RangeError(`${name} must be a finite number, got ${String(value)}`);
    }
}
function assertNonNegative(name, value) {
    assertFinite(name, value);
    if (value < 0) {
        throw new RangeError(`${name} must be >= 0, got ${value}`);
    }
}
function assertRatio(name, value) {
    assertFinite(name, value);
    if (value < 0 || value > 1) {
        throw new RangeError(`${name} must be in [0, 1], got ${value}`);
    }
}
function assertJobMaxLvBound(jobMaxLv) {
    assertNonNegative('jobMaxLv', jobMaxLv);
    if (jobMaxLv > MAX_LEVEL_ASYMPTOTE) {
        throw new RangeError(`jobMaxLv must be <= ${MAX_LEVEL_ASYMPTOTE}, got ${jobMaxLv}`);
    }
}
/**
 * Single (job, stat) permanent contribution.
 * Returns growth_value * A * statContribution(jobMaxLv, tier).
 * docs/06-level-xp §6.12 / ADR 0006 §2.2.3.
 *
 * The Tier curve is shared with the current-job stat scaling
 * (packages/sim/src/stats-resolver.ts) so a job grinded to high jobMaxLv
 * contributes more permanent bonus when its Tier sustains growth at high L.
 */
export function permanentStatContribution(jobMaxLv, growth, tier) {
    assertJobMaxLvBound(jobMaxLv);
    if (jobMaxLv === 0)
        return 0;
    const growthValue = STAT_GROWTH_VALUE[growth];
    return growthValue * PERMANENT_STAT_BONUS_COEFF * statContribution(jobMaxLv, tier);
}
/**
 * Starting job-level after switching into a job.
 * - Untouched job (jobMaxLv === 0) -> 1
 * - Visited job -> floor(jobMaxLv * ratio), default ratio = 0.5
 * docs/03-jobs-skills §3.5, docs/06-level-xp §6.6, ADR 0004 §3.5.
 *
 * Scrolls (Common/Magic/Rare/Unique/Mythic) raise the ratio via
 * restartLevelForWithScroll. Mythic = 1.0 means restart at jobMaxLv exactly.
 */
export function restartLevelFor(jobMaxLv, ratio = RESTART_RATIO_DEFAULT) {
    assertJobMaxLvBound(jobMaxLv);
    assertRatio('ratio', ratio);
    if (jobMaxLv === 0)
        return 1;
    return Math.floor(jobMaxLv * ratio);
}
/**
 * Thin wrapper that resolves a scroll kind to its ratio.
 * - scrollKind === undefined -> default ratio 0.5
 * - Unknown scroll kind throws RangeError
 * ADR 0004 §3.5.
 */
export function restartLevelForWithScroll(jobMaxLv, scrollKind) {
    if (scrollKind === undefined) {
        return restartLevelFor(jobMaxLv);
    }
    const ratio = RESTART_RATIO_BY_SCROLL[scrollKind];
    if (ratio === undefined) {
        throw new RangeError(`unknown scrollKind: ${String(scrollKind)}`);
    }
    return restartLevelFor(jobMaxLv, ratio);
}
/**
 * XP multiplier inside the re-promotion bonus band.
 * Returns JOB_XP_BONUS_MULTIPLIER iff:
 *   - jobMaxLv > 0
 *   - ratio < 1.0 (Mythic ratio = 1.0 collapses the band; bonus zone is empty)
 *   - floor(jobMaxLv * ratio) <= currentJobLv <= jobMaxLv
 *
 * Returns 1.0 otherwise. Note that currentJobLv > jobMaxLv (player progressing past
 * their previous peak and updating jobMaxLv) is normal post-bonus state, not an error.
 *
 * docs/06-level-xp §6.6, ADR 0004 §4.4.
 */
export function jobXpBonusMultiplier(currentJobLv, jobMaxLv, ratio = RESTART_RATIO_DEFAULT) {
    assertNonNegative('currentJobLv', currentJobLv);
    assertNonNegative('jobMaxLv', jobMaxLv);
    assertRatio('ratio', ratio);
    if (jobMaxLv === 0)
        return 1;
    // ratio = 1.0 -> Mythic scroll: restart point equals jobMaxLv, zone empty by spec
    if (ratio >= 1)
        return 1;
    const start = Math.floor(jobMaxLv * ratio);
    if (currentJobLv >= start && currentJobLv <= jobMaxLv) {
        return JOB_XP_BONUS_MULTIPLIER;
    }
    return 1;
}
function emptyStatBonus() {
    return STAT_KEYS.reduce((acc, key) => {
        acc[key] = 0;
        return acc;
    }, { str: 0, dex: 0, int: 0, vit: 0, wis: 0 });
}
/**
 * Permanent stat bonus from past job max levels.
 * Sum_jobs( permanentStatContribution(jobMaxLv, jobGrowth[stat], jobTier) ) per stat.
 * docs/06-level-xp §6.12, ADR 0006 §2.2.3.
 *
 * Unknown jobIds in jobHistory are silently skipped (zero contribution).
 * Jobs without a `growth` band are skipped (catalogue scaffolds).
 */
export function permanentStatBonusFromHistory(jobHistory, jobDefs) {
    const bonus = emptyStatBonus();
    for (const [jobId, entry] of Object.entries(jobHistory)) {
        assertJobMaxLvBound(entry.maxLv);
        const def = jobDefs.get(jobId);
        if (!def)
            continue;
        if (!def.growth)
            continue; // catalogue scaffolds without growth contribute nothing
        for (const stat of STAT_KEYS) {
            const growth = def.growth[stat];
            bonus[stat] += permanentStatContribution(entry.maxLv, growth, def.tier);
        }
    }
    return bonus;
}
//# sourceMappingURL=job-xp.js.map